import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const DentalHistoryForm = ({ data, onUpdate }) => {
  const [formData, setFormData] = useState({
    lastDentalVisit: data?.dentalHistory?.lastDentalVisit || '',
    previousDentist: data?.dentalHistory?.previousDentist || '',
    dentalConcerns: data?.dentalHistory?.dentalConcerns || [],
    painLevel: data?.dentalHistory?.painLevel || '0',
    oralHygieneFrequency: data?.dentalHistory?.oralHygieneFrequency || '',
    ...data?.dentalHistory
  });

  const dentalConcerns = [
    'Tooth Pain', 'Bleeding Gums', 'Sensitivity', 'Bad Breath',
    'Loose Teeth', 'Jaw Pain', 'Clicking Jaw', 'Grinding Teeth',
    'Cosmetic Concerns', 'Missing Teeth', 'Broken Teeth', 'Cavities'
  ];

  const painLevels = [
    { value: '0', label: '0 - No Pain' },
    { value: '1', label: '1 - Minimal' },
    { value: '2', label: '2 - Mild' },
    { value: '3', label: '3 - Uncomfortable' },
    { value: '4', label: '4 - Moderate' },
    { value: '5', label: '5 - Distracting' },
    { value: '6', label: '6 - Distressing' },
    { value: '7', label: '7 - Intense' },
    { value: '8', label: '8 - Very Intense' },
    { value: '9', label: '9 - Severe' },
    { value: '10', label: '10 - Worst Possible' }
  ];

  const hygieneFrequencies = [
    { value: 'twice-daily', label: 'Twice Daily' },
    { value: 'once-daily', label: 'Once Daily' },
    { value: 'few-times-week', label: 'Few Times a Week' },
    { value: 'occasionally', label: 'Occasionally' }
  ];

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate('dentalHistory', updated);
  };

  const handleConcernToggle = (concern) => {
    const updated = formData?.dentalConcerns?.includes(concern)
      ? formData?.dentalConcerns?.filter(c => c !== concern)
      : [...formData?.dentalConcerns, concern];
    handleChange('dentalConcerns', updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Activity" size={24} className="text-primary" />
          Dental History
        </h2>
        <p className="text-sm text-muted-foreground">
          Help us understand your oral health history and current concerns.
        </p>
      </div>

      {/* Last Dental Visit */}
      <Input
        label="When was your last dental visit?"
        type="date"
        value={formData?.lastDentalVisit}
        onChange={(e) => handleChange('lastDentalVisit', e?.target?.value)}
      />

      {/* Previous Dentist */}
      <Input
        label="Previous Dentist (if applicable)"
        type="text"
        value={formData?.previousDentist}
        onChange={(e) => handleChange('previousDentist', e?.target?.value)}
        placeholder="Dr. Name, Practice Name"
      />

      {/* Dental Concerns */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          What dental concerns do you have? (Select all that apply)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {dentalConcerns?.map((concern) => (
            <div key={concern} className="flex items-center gap-2">
              <Checkbox
                checked={formData?.dentalConcerns?.includes(concern)}
                onChange={() => handleConcernToggle(concern)}
              />
              <label className="text-sm text-foreground cursor-pointer">
                {concern}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Pain Level */}
      <div>
        <Select
          label="Current Pain Level"
          options={painLevels}
          value={formData?.painLevel}
          onChange={(value) => handleChange('painLevel', value)}
          description="Rate your current dental pain on a scale of 0-10"
        />
        
        {/* Visual Pain Scale */}
        <div className="mt-3 flex items-center gap-2">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((level) => (
            <button
              key={level}
              onClick={() => handleChange('painLevel', level?.toString())}
              className={`flex-1 h-8 rounded text-xs font-medium transition-all ${
                parseInt(formData?.painLevel) === level
                  ? 'bg-primary text-primary-foreground'
                  : level <= 3
                  ? 'bg-success/20 text-success hover:bg-success/30'
                  : level <= 6
                  ? 'bg-warning/20 text-warning hover:bg-warning/30' :'bg-destructive/20 text-destructive hover:bg-destructive/30'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Oral Hygiene Habits */}
      <Select
        label="How often do you brush your teeth?"
        options={hygieneFrequencies}
        value={formData?.oralHygieneFrequency}
        onChange={(value) => handleChange('oralHygieneFrequency', value)}
        placeholder="Select frequency"
      />

      {/* Flossing Habits */}
      <div className="flex items-center gap-2">
        <Checkbox
          checked={formData?.flossRegularly || false}
          onChange={(e) => handleChange('flossRegularly', e?.target?.checked)}
        />
        <label className="text-sm text-foreground">
          I floss regularly
        </label>
      </div>

      {/* Previous Treatments */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Previous Dental Treatments
        </label>
        <textarea
          value={formData?.previousTreatments || ''}
          onChange={(e) => handleChange('previousTreatments', e?.target?.value)}
          placeholder="List any previous dental work (fillings, crowns, root canals, orthodontics, etc.)..."
          className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>

      {/* Aesthetic Concerns */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Aesthetic Concerns
        </label>
        <textarea
          value={formData?.aestheticConcerns || ''}
          onChange={(e) => handleChange('aestheticConcerns', e?.target?.value)}
          placeholder="Describe any concerns about the appearance of your teeth or smile..."
          className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>
    </div>
  );
};

export default DentalHistoryForm;